import { getUrlPathname, isAbsoluteUrl } from "~/utils/url";
import type { LinkFragment } from "~~/graphql/fragments/link-fragment";

export type ResolvedLink = {
  href: string;
  external: boolean;
  target: string | undefined;
};

/**
 * Resolve a Craft Link field value to a frontend href.
 *
 * Entry links come back as absolute URLs on the frontend domain — reduce them
 * to their pathname so they stay locale-/domain-agnostic. URL links pass
 * through (absolute external URLs, root-relative paths, anchors).
 */
/** Visible link text: the editor's label, else Craft's default label. */
export function linkLabel(link: LinkFragment | null): string {
  return link?.label || link?.defaultLabel || "";
}

export function resolveLink(link: LinkFragment | null): ResolvedLink | null {
  if (!link?.url) return null;

  // Fragment anchors ("#newsletter", or a bare "#" editors use while the
  // destination is still open) must render as a plain <a href="#…">: routing
  // them through localePath/NuxtLink yields an empty `to` and an href-less
  // anchor. `external: true` selects the plain-<a> branch.
  if (link.url.startsWith("#")) {
    return { href: link.url, external: true, target: undefined };
  }

  if (link.type === "entry") {
    return {
      href: isAbsoluteUrl(link.url) ? getUrlPathname(link.url) : link.url,
      external: false,
      target: undefined,
    };
  }

  const external = isAbsoluteUrl(link.url);
  return {
    href: link.url,
    external,
    target: link.target ?? (external ? "_blank" : undefined),
  };
}
